/**
 * 版本更新检测 Hook
 *
 * 检测时机（无轮询，按需检测）：
 * 1. 页面可见性变化（用户切回页面）
 * 2. 路由切换
 * 3. 手动触发
 *
 * 工作原理：
 * 1. 页面加载时获取当前版本并缓存
 * 2. 在检测时机请求 /version.json（带时间戳绕过缓存）
 * 3. 对比版本号，不同则触发更新提示
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export interface VersionInfo {
  version: string;
  buildTime: number;
  name?: string;
}

export interface UseVersionCheckOptions {
  /** 是否在路由切换时检测（默认 true） */
  checkOnRouteChange?: boolean;
  /** 是否在页面可见性变化时检测（默认 true） */
  checkOnVisibilityChange?: boolean;
  /** 最小检测间隔（毫秒，默认 60000 = 1分钟） */
  minCheckInterval?: number;
  /** 是否启用（默认 true） */
  enabled?: boolean;
}

export interface UseVersionCheckReturn {
  /** 是否有新版本可用 */
  hasUpdate: boolean;
  /** 当前版本 */
  currentVersion: string | null;
  /** 新版本 */
  newVersion: string | null;
  /** 刷新页面获取新版本 */
  refresh: () => void;
  /** 关闭更新提示 */
  dismiss: () => void;
  /** 手动检测更新 */
  checkNow: () => Promise<void>;
}

const VERSION_URL = '/version.json';

export function useVersionCheck(options: UseVersionCheckOptions = {}): UseVersionCheckReturn {
  const {
    checkOnRouteChange = true,
    checkOnVisibilityChange = true,
    minCheckInterval = 60000, // 1分钟
    enabled = true,
  } = options;

  const [hasUpdate, setHasUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const lastCheckTimeRef = useRef(0);
  const location = useLocation();

  // 获取版本信息
  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      // 添加时间戳绕过缓存
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.warn('[VersionCheck] Failed to fetch version:', error);
      return null;
    }
  }, []);

  // 检测更新
  const checkUpdate = useCallback(async () => {
    if (!enabled) return;

    // 检查最小间隔
    const now = Date.now();
    if (now - lastCheckTimeRef.current < minCheckInterval) {
      return;
    }
    lastCheckTimeRef.current = now;

    const remoteVersion = await fetchVersion();
    if (!remoteVersion) return;

    // 首次加载，设置当前版本
    if (!currentVersion) {
      setCurrentVersion(remoteVersion.version);
      return;
    }

    // 版本对比
    if (remoteVersion.version !== currentVersion && !dismissed) {
      console.log('[VersionCheck] New version available:', remoteVersion.version);
      setNewVersion(remoteVersion.version);
      setHasUpdate(true);
    }
  }, [enabled, currentVersion, dismissed, fetchVersion, minCheckInterval]);

  // 刷新页面
  const refresh = useCallback(() => {
    // 强制刷新，绕过缓存
    window.location.reload();
  }, []);

  // 关闭提示
  const dismiss = useCallback(() => {
    setDismissed(true);
    setHasUpdate(false);
  }, []);

  // 手动检测
  const checkNow = useCallback(async () => {
    lastCheckTimeRef.current = 0; // 重置间隔限制
    await checkUpdate();
  }, [checkUpdate]);

  // 初始化：获取当前版本
  useEffect(() => {
    if (!enabled) return;

    fetchVersion().then((info) => {
      if (info) {
        setCurrentVersion(info.version);
        console.log('[VersionCheck] Current version:', info.version);
      }
    });
  }, [enabled, fetchVersion]);

  // 页面可见性变化检测
  useEffect(() => {
    if (!enabled || !checkOnVisibilityChange) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, checkOnVisibilityChange, checkUpdate]);

  // 路由切换检测
  useEffect(() => {
    if (!enabled || !checkOnRouteChange) return;
    checkUpdate();
  }, [location.pathname, enabled, checkOnRouteChange, checkUpdate]);

  return {
    hasUpdate: hasUpdate && !dismissed,
    currentVersion,
    newVersion,
    refresh,
    dismiss,
    checkNow,
  };
}
